import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Register from './pages/Auth/Register';
import PendingApproval from './pages/Auth/PendingApproval';
import LoadingSpinner from './components/UI/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import Forbidden from './pages/Errors/Forbidden';
import RoutePerformanceTracker from './components/UI/RoutePerformanceTracker';

// Helper for lazy loading dynamic imports with auto-retry on ChunkLoadError
const lazyWithRetry = (componentImport) =>
    lazy(async () => {
        const pageHasBeenRefreshed = window.sessionStorage.getItem('page-has-been-refreshed');
        try {
            const component = await componentImport();
            window.sessionStorage.removeItem('page-has-been-refreshed');
            return component;
        } catch (error) {
            if (!pageHasBeenRefreshed) {
                window.sessionStorage.setItem('page-has-been-refreshed', 'true');
                window.location.reload();
                return { default: () => null };
            }
            throw error;
        }
    });

// Lazy load routes for code splitting
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/Dashboard'));
const Leads = lazyWithRetry(() => import('./pages/Leads/Leads'));
const CreateLead = lazyWithRetry(() => import('./pages/Leads/CreateLead'));
const Users = lazyWithRetry(() => import('./pages/Users/Users'));
const UserDetails = lazyWithRetry(() => import('./pages/Users/UserDetails'));
const EditUser = lazyWithRetry(() => import('./pages/Users/EditUser'));
const CreateUser = lazyWithRetry(() => import('./pages/Users/CreateUser'));
const TeamManagement = lazyWithRetry(() => import('./pages/Users/TeamManagement'));
const PendingApprovals = lazyWithRetry(() => import('./pages/Users/PendingApprovals'));
const Projects = lazyWithRetry(() => import('./pages/Projects/Projects'));
const ProjectDetails = lazyWithRetry(() => import('./pages/Projects/ProjectDetails'));
const CreateProject = lazyWithRetry(() => import('./pages/Projects/CreateProject'));
const Locations = lazyWithRetry(() => import('./pages/Locations/Locations'));
const LocationDetails = lazyWithRetry(() => import('./pages/Locations/LocationDetails'));
// Legacy imports (kept for backward compatibility)
const Plots = lazyWithRetry(() => import('./pages/Plots/Plots'));
const PlotDetails = lazyWithRetry(() => import('./pages/Plots/PlotDetails'));
const CreatePlot = lazyWithRetry(() => import('./pages/Plots/CreatePlot'));
const Customers = lazyWithRetry(() => import('./pages/Customers/Customers'));
const CustomerDetails = lazyWithRetry(() => import('./pages/Customers/CustomerDetails'));
const CreateCustomer = lazyWithRetry(() => import('./pages/Customers/CreateCustomer'));
const Reports = lazyWithRetry(() => import('./pages/Reports/Reports'));
const ScheduledReports = lazyWithRetry(() => import('./pages/Reports/ScheduledReports'));
const AttendanceDashboard = lazyWithRetry(() => import('./pages/Attendance/AttendanceDashboard'));
const Profile = lazyWithRetry(() => import('./pages/Profile/Profile'));
const InventoryDashboard = lazyWithRetry(() => import('./pages/Inventory/InventoryDashboard'));
const Settings = lazyWithRetry(() => import('./pages/Settings/Settings'));
const RolesPermissions = lazyWithRetry(() => import('./pages/Settings/RolesPermissions'));
const Integrations = lazyWithRetry(() => import('./pages/Settings/Integrations'));
const FollowUpDashboard = lazyWithRetry(() => import('./pages/FollowUps/FollowUpDashboard'));
const FollowUpDetail = lazyWithRetry(() => import('./pages/FollowUps/FollowUpDetail'));
const SiteVisitDashboard = lazyWithRetry(() => import('./pages/SiteVisits/SiteVisitDashboard'));
const SiteVisitDetail = lazyWithRetry(() => import('./pages/SiteVisits/SiteVisitDetail'));
const CommunicationDashboard = lazyWithRetry(() => import('./pages/Communications/CommunicationDashboard'));
const LeadReassignmentDashboard = lazyWithRetry(() => import('./pages/Leads/LeadReassignmentDashboard'));
const PostSalesDashboard = lazyWithRetry(() => import('./pages/PostSales/PostSalesDashboard'));
const PostSalesBookings = lazyWithRetry(() => import('./pages/PostSales/Bookings'));
const BookingDetail = lazyWithRetry(() => import('./pages/PostSales/Bookings/BookingDetail'));
const PostSalesPayments = lazyWithRetry(() => import('./pages/PostSales/Payments'));
const PostSalesAgreements = lazyWithRetry(() => import('./pages/PostSales/Agreements'));
const PostSalesKYC = lazyWithRetry(() => import('./pages/PostSales/KYC'));
const PostSalesLoans = lazyWithRetry(() => import('./pages/PostSales/Loans'));
const PostSalesConstruction = lazyWithRetry(() => import('./pages/PostSales/Construction'));
const PostSalesTickets = lazyWithRetry(() => import('./pages/PostSales/Tickets'));
const PostSalesNOC = lazyWithRetry(() => import('./pages/PostSales/NOC'));
const PostSalesPossession = lazyWithRetry(() => import('./pages/PostSales/Possession'));
const PostSalesCommunication = lazyWithRetry(() => import('./pages/PostSales/Communication'));
const PostSalesReports = lazyWithRetry(() => import('./pages/PostSales/Reports'));
const BrokerResalePortal = lazyWithRetry(() => import('./pages/Resale/BrokerResalePortal'));
const MarketingAutomationDashboard = lazyWithRetry(() => import('./pages/Marketing/MarketingAutomationDashboard'));
const IntegrationSetup = lazyWithRetry(() => import('./pages/Marketing/IntegrationSetup'));
const LiveTracking = lazyWithRetry(() => import('./pages/Tracking/LiveTracking'));
const Debug = lazyWithRetry(() => import('./pages/Debug/Debug'));

// Wrap Routes with Sentry routing for better error tracking
const SentryRoutes = Sentry.withSentryRouting(Routes);

function App() {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    if (!user) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/pending-approval" element={<PendingApproval />} />
                <Route path="/register" element={<Register />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    return (
        <Layout>
            <RoutePerformanceTracker />
            <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center">
                    <LoadingSpinner size="large" />
                </div>
            }>
                <SentryRoutes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<ProtectedRoute requiredPermission="dashboard.view"><Dashboard /></ProtectedRoute>} />

                    {/* Leads Routes */}
                    <Route path="/leads/*" element={<ProtectedRoute requiredPermission="leads.view"><Leads /></ProtectedRoute>} />
                    <Route path="/leads/create" element={<ProtectedRoute requiredPermission="leads.create"><CreateLead /></ProtectedRoute>} />

                    {/* Auth Routes - Register (accessible to all, but creates pending users) */}
                    <Route path="/register" element={<Register />} />

                    {/* Users Routes */}
                    <Route path="/users" element={<ProtectedRoute requiredPermission="users.view"><Users /></ProtectedRoute>} />
                    <Route path="/users/create" element={<ProtectedRoute requiredPermission="users.create"><CreateUser /></ProtectedRoute>} />
                    <Route path="/users/pending-approvals" element={<ProtectedRoute requiredPermission="users.view"><PendingApprovals /></ProtectedRoute>} />
                    <Route path="/users/:userId" element={<ProtectedRoute requiredPermission="users.view"><UserDetails /></ProtectedRoute>} />
                    <Route path="/users/:userId/edit" element={<ProtectedRoute requiredPermission="users.edit"><EditUser /></ProtectedRoute>} />
                    <Route path="/users/:userId/team" element={<ProtectedRoute requiredPermission="users.view"><TeamManagement /></ProtectedRoute>} />

                    {/* Projects Routes - Unified */}
                    <Route path="/projects" element={<ProtectedRoute requiredPermission="projects.view"><Projects /></ProtectedRoute>} />
                    <Route path="/projects/create" element={<ProtectedRoute requiredPermission="projects.create"><CreateProject /></ProtectedRoute>} />
                    <Route path="/projects/:projectId" element={<ProtectedRoute requiredPermission="projects.view"><ProjectDetails /></ProtectedRoute>} />
                    <Route path="/projects/:projectId/edit" element={<ProtectedRoute requiredPermission="projects.edit"><ProjectDetails /></ProtectedRoute>} />
                    {/* Legacy Properties / Units tab now lives inside Project Details */}
                    <Route path="/projects/:projectId/properties" element={<ProtectedRoute requiredPermission="projects.view"><ProjectDetails /></ProtectedRoute>} />

                    {/* Locations Routes */}
                    <Route path="/locations" element={<ProtectedRoute requiredPermission="locations.view"><Locations /></ProtectedRoute>} />
                    <Route path="/locations/:locationId" element={<ProtectedRoute requiredPermission="locations.view"><LocationDetails /></ProtectedRoute>} />

                    {/* Legacy Routes (kept for backward compatibility) */}
                    <Route path="/plots" element={<ProtectedRoute requiredPermission="plots.view"><Plots /></ProtectedRoute>} />
                    <Route path="/plots/create" element={<ProtectedRoute requiredPermission="plots.create"><CreatePlot /></ProtectedRoute>} />
                    <Route path="/plots/:plotId" element={<ProtectedRoute requiredPermission="plots.view"><PlotDetails /></ProtectedRoute>} />

                    {/* Customers Routes */}
                    <Route path="/customers" element={<ProtectedRoute requiredPermission="customers.view"><Customers /></ProtectedRoute>} />
                    <Route path="/customers/create" element={<ProtectedRoute requiredPermission="customers.create"><CreateCustomer /></ProtectedRoute>} />
                    <Route path="/customers/:customerId" element={<ProtectedRoute requiredPermission="customers.view"><CustomerDetails /></ProtectedRoute>} />
                    <Route path="/customers/:customerId/edit" element={<ProtectedRoute requiredPermission="customers.edit"><CustomerDetails /></ProtectedRoute>} />

                    {/* Reports Routes */}
                    <Route path="/reports" element={<ProtectedRoute requiredPermission="reports.view"><Reports /></ProtectedRoute>} />
                    <Route path="/scheduled-reports" element={<ProtectedRoute requiredPermissions={['reports.view', 'reports.schedule']}><ScheduledReports /></ProtectedRoute>} />
                    <Route path="/attendance" element={<ProtectedRoute><AttendanceDashboard /></ProtectedRoute>} />

                    {/* Live Tracking */}
                    <Route path="/tracking/live" element={<ProtectedRoute requiredPermission="tracking.view"><LiveTracking /></ProtectedRoute>} />

                    {/* Inventory Routes */}
                    <Route path="/inventory" element={<ProtectedRoute requiredPermission="inventory.view"><InventoryDashboard /></ProtectedRoute>} />

                    {/* Settings Routes */}
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/settings/roles-permissions" element={<ProtectedRoute requiredPermission="roles.view"><RolesPermissions /></ProtectedRoute>} />
                    <Route path="/settings/integrations" element={<ProtectedRoute requiredPermission="settings.view"><Integrations /></ProtectedRoute>} />

                    {/* Advanced CRM Features */}
                    <Route path="/followups" element={<ProtectedRoute requiredPermission="followups.view"><FollowUpDashboard /></ProtectedRoute>} />
                    <Route path="/followups/:id" element={<ProtectedRoute requiredPermission="followups.view"><FollowUpDetail /></ProtectedRoute>} />
                    <Route path="/sitevisits" element={<ProtectedRoute requiredPermission="sitevisits.view"><SiteVisitDashboard /></ProtectedRoute>} />
                    <Route path="/sitevisits/:id" element={<ProtectedRoute requiredPermission="sitevisits.view"><SiteVisitDetail /></ProtectedRoute>} />
                    <Route path="/communications" element={<ProtectedRoute requiredPermission="communications.view"><CommunicationDashboard /></ProtectedRoute>} />

                    {/* Enhanced Features */}
                    <Route path="/lead-reassignment" element={<ProtectedRoute requiredPermissions={['leads.assign', 'leads.view']}><LeadReassignmentDashboard /></ProtectedRoute>} />
                    <Route path="/post-sales" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesDashboard /></ProtectedRoute>} />
                    <Route path="/post-sales/bookings" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesBookings /></ProtectedRoute>} />
                    <Route path="/post-sales/bookings/:id" element={<ProtectedRoute requiredPermission="postsales.view"><BookingDetail /></ProtectedRoute>} />
                    <Route path="/post-sales/payments" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesPayments /></ProtectedRoute>} />
                    <Route path="/post-sales/agreements" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesAgreements /></ProtectedRoute>} />
                    <Route path="/post-sales/kyc" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesKYC /></ProtectedRoute>} />
                    <Route path="/post-sales/loans" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesLoans /></ProtectedRoute>} />
                    <Route path="/post-sales/construction" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesConstruction /></ProtectedRoute>} />
                    <Route path="/post-sales/noc" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesNOC /></ProtectedRoute>} />
                    <Route path="/post-sales/possession" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesPossession /></ProtectedRoute>} />
                    <Route path="/post-sales/tickets" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesTickets /></ProtectedRoute>} />
                    <Route path="/post-sales/communication" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesCommunication /></ProtectedRoute>} />
                    <Route path="/post-sales/reports" element={<ProtectedRoute requiredPermission="postsales.view"><PostSalesReports /></ProtectedRoute>} />
                    <Route path="/resale-portal" element={<ProtectedRoute requiredPermission="brokerportal.view"><BrokerResalePortal /></ProtectedRoute>} />
                    <Route path="/marketing-automation" element={<ProtectedRoute requiredPermission="marketing.view"><MarketingAutomationDashboard /></ProtectedRoute>} />
                    <Route path="/marketing-automation/integrations/:type" element={<ProtectedRoute requiredPermission="marketing.view"><IntegrationSetup /></ProtectedRoute>} />

                    {/* Profile Routes */}
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                    {/* Error Pages */}
                    <Route path="/403" element={<Forbidden />} />

                    {/* Debug Route */}
                    <Route path="/debug" element={<Debug />} />

                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </SentryRoutes>
            </Suspense>
        </Layout>
    );
}

export default App;
